import React from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { HiArrowNarrowRight } from "react-icons/hi";
import {
  HiChevronRight,
  HiOutlineChartBar,
  HiOutlineEye,
  HiOutlineMegaphone,
  HiOutlinePresentationChartLine,
  HiOutlineSpeakerWave,
} from "react-icons/hi2";

const CampaignContent = () => {
  const location = useLocation();
  const isOverviewPage = location.pathname === "/company/dashboard/campaigns";
  const setupSteps = [
    {
      title: "Create Campaign",
      description:
        "Launch a new marketing campaign. Set up goals, targeting, commission structures, and campaign duration with start and end dates.",
      ctaLabel: "Create",
      ctaPath: "/company/dashboard/campaigns/create",
      icon: HiOutlineMegaphone,
    },
    {
      title: "View Campaigns",
      description:
        "View and manage all your active campaigns. Monitor performance and track results.",
      ctaLabel: "View",
      ctaPath: "/company/dashboard/campaigns/",
      icon: HiOutlineEye,
    },
    {
      title: "Campaign Analytics",
      description:
        "Access detailed analytics and insights about your campaign performance.",
      ctaLabel: "Analytics",
      ctaPath: "/company/dashboard/campaigns/analytics",
      icon: HiOutlineChartBar,
    },
    {
      title: "Create Campaign Advertisement",
      description:
        "Create and manage your campaign advertisements. Set up goals, targeting, commission structures, and campaign duration with start and end dates.",
      ctaLabel: "Create",
      ctaPath: "/company/dashboard/campaigns/advertising/create",
      icon: HiOutlineSpeakerWave,
    },
    {
      title: "View Campaign Advertisement",
      description:
        "View and manage your campaign advertisements. Set up goals, targeting, commission structures, and campaign duration with start and end dates.",
      ctaLabel: "View",
      ctaPath: "/company/dashboard/campaigns/advertising/view",
      icon: HiOutlinePresentationChartLine,
    },
  ];

  // Helper function to truncate content
  const truncateText = (text, wordLimit) => {
    const words = text.split(" ");
    if (words.length > wordLimit) {
      return words.slice(0, wordLimit).join(" ") + "...";
    }
    return text;
  };

  if (!isOverviewPage) {
    return <Outlet />;
  }

  return (
    <div className="bg-gray-100 min-h-full w-full flex flex-col">
      <div className="flex flex-col lg:flex-row flex-1">
        <div className="flex-1 p-0 md:p-3 lg:p-4">
          <div className="bg-white border border-gray-100 shadow-sm p-2 mb-4 md:hidden">
            <h2 className="text-lg leading-7 font-bold mb-2">Campaign Management</h2>
            <p className="text-xs text-gray-500 mb-4">
              Create and manage your marketing campaigns effectively.
            </p>

            <div className="flex items-center justify-between mb-4">
              {setupSteps.map((_, index) => (
                <React.Fragment key={`campaign-mobile-step-${index + 1}`}>
                  <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[11px] font-semibold flex items-center justify-center">
                    {index + 1}
                  </span>
                  {index < setupSteps.length - 1 && (
                    <HiArrowNarrowRight className="text-orange-500 text-lg shrink-0" />
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="space-y-3">
              {setupSteps.map((step) => {
                const StepIcon = step.icon;

                return (
                  <div
                    key={step.title}
                    className="rounded-xl border border-gray-200 bg-white p-3 shadow-[0_1px_2px_rgba(16,24,40,0.06)]"
                  >
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 rounded-full bg-orange-50 text-orange-500 flex items-center justify-center shrink-0">
                        <StepIcon className="text-lg" />
                      </span>
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-base font-semibold leading-5">
                            {step.title}
                          </h3>
                          <HiChevronRight className="text-gray-300 text-lg shrink-0 mt-0.5" />
                        </div>
                        <p className="text-[10px] text-gray-500 mt-1.5 leading-4">
                          {truncateText(step.description, 18)}
                        </p>
                        <div className="mt-3 flex justify-end">
                          <Link
                            to={step.ctaPath}
                            className="inline-flex items-center justify-center rounded-md bg-orange-500 px-3 py-1.5 text-[11px] font-semibold text-white"
                          >
                            {step.ctaLabel}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="hidden md:block bg-white shadow-md rounded-lg p-4 lg:p-5 mb-10 lg:mb-0">
            <h2 className="text-lg font-bold mb-2">Campaign Management</h2>
            <p className="text-[10px] md:text-sm text-gray-500 mb-5">
              Create and manage your marketing campaigns effectively.
            </p>

            {/* Setup Steps */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
             

              {/* Create Campaign */}
              <div className="p-5 border rounded-lg flex flex-col h-full">
                <h3 className="font-semibold mb-2">Create Campaign</h3>
                <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
                  {truncateText(
                    "Launch a new marketing campaign. Set up goals, targeting, commission structures, and campaign duration with start and end dates.",
                    20
                  )}
                </p>
                <Link to="/company/dashboard/campaigns/create" className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto">
                  Create
                </Link>
              </div>

               {/* View Campaigns */}
               <div className="p-5 border rounded-lg flex flex-col h-full">
                <h3 className="font-semibold mb-2">View Campaigns</h3>
                <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
                  {truncateText(
                    "View and manage all your active campaigns. Monitor performance and track results.",
                    20
                  )}
                </p>
                <Link to="/company/dashboard/campaigns/" className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto">
                  View
                </Link>
              </div>

              {/* Campaign Analytics */}
              <div className="p-5 border rounded-lg flex flex-col h-full">
                <h3 className="font-semibold mb-2">Campaign Analytics</h3>
                <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
                  {truncateText(
                    "Access detailed analytics and insights about your campaign performance.",
                    20
                  )}
                </p>
                <Link to="/company/dashboard/campaigns/analytics" className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto">
                  Analytics
                </Link>
              </div>

               {/* Advertising Campaign  */}
               <div className="p-5 border rounded-lg flex flex-col h-full">
                <h3 className="font-semibold mb-2">Create Campaign Advertisement</h3>
                <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
                  {truncateText(
                    "Create and manage your campaign advertisements. Set up goals, targeting, commission structures, and campaign duration with start and end dates.",
                    20
                  )}
                </p>
                <Link to="/company/dashboard/campaigns/advertising/create" className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto">
                  Create
                </Link>
              </div>

               {/* View Advertising Campaign  */}
               <div className="p-5 border rounded-lg flex flex-col h-full">
                <h3 className="font-semibold mb-2">View Campaign Advertisement</h3>
                <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
                  {truncateText(
                    "View and manage your campaign advertisements. Set up goals, targeting, commission structures, and campaign duration with start and end dates.",
                    20
                  )}
                </p>
                <Link to="/company/dashboard/campaigns/advertising/view" className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto">
                  View
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CampaignContent;